import Modal from "@/components/atoms/Modal";
import { SelectPaginateAsync } from "@/components/atoms/Select";
import { INVALID_FORM_MESSAGE } from "@/constants/validatorMessages";
import useLoadOptions from "@/hooks/useLoadOptions";
import EmailTemplate from "@/interfaces/EmailTemplate";
import { IModalChildrenProps, IModalMethods } from "@/interfaces/Modal";
import TrainingBrochure from "@/interfaces/TrainingBrochure";
import { useSendCompanyBrochuresMutation } from "@/services/companies/emails";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import {
  ElementRef,
  ForwardRefRenderFunction,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import { Button as BsButton, Col, Form, Row } from "react-bootstrap";
import {
  Controller,
  SubmitErrorHandler,
  SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { ToastContentProps, toast } from "react-toastify";

interface Programme {
  label: string;
  url: string;
}

interface SendBrochuresForm {
  brochures: TrainingBrochure[];
  emailTemplate: EmailTemplate;
  programmes: Programme[];
}

const SendCompanyBrochuresModal: ForwardRefRenderFunction<
  IModalMethods,
  IModalChildrenProps<{ companyId: string }>
> = ({ companyId, onHide, callback }, ref) => {
  const ModalRef = useRef<ElementRef<typeof Modal>>(null);
  const {
    control,
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<SendBrochuresForm>({
    defaultValues: { brochures: [], programmes: [] },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "programmes",
  });

  const [sendBrochuresAsync] = useSendCompanyBrochuresMutation();

  const { loadTrainingBrochureOptions, loadEmailTemplatesOptions } =
    useLoadOptions();

  const showOrHiddenModal = () => ModalRef.current?.showOrHiddenModal();

  useImperativeHandle(ref, () => ({
    showOrHiddenModal,
  }));

  const onSubmit: SubmitHandler<SendBrochuresForm> = (data) => {
    showOrHiddenModal();
    const brochureIds = data.brochures.map((b) => b.id);
    const templateId = data.emailTemplate.templateId;
    const programmes = data.programmes?.length ? data.programmes : undefined;

    toast.promise(
      sendBrochuresAsync([companyId, { brochureIds, templateId, programmes }]).unwrap(),
      {
        pending: "Envoi de l'email en cours...",
        success: {
          render({ data }) {
            reset();
            callback?.(data);
            return (
              <p style={{ marginBottom: 0, textAlign: "center" }}>
                L'email a été envoyé avec succès 🎉
              </p>
            );
          },
        },
        error: {
          render({ data }: ToastContentProps<FetchBaseQueryError>) {
            showOrHiddenModal();
            if (data?.status === 422) {
              return "Les champs que vous avez remplis semblent être incorrects.";
            }
            if (data?.status === 400) {
              return "L'entreprise n'a pas d'adresse email configurée.";
            }
            return "Une erreur est survenue lors de l'envoi";
          },
        },
      }
    );
  };

  const onError: SubmitErrorHandler<SendBrochuresForm> = () =>
    toast.error(INVALID_FORM_MESSAGE);

  return (
    <Modal
      ref={ModalRef}
      onHide={onHide}
      title="Envoyer des plaquettes de formation"
      onSubmit={handleSubmit(onSubmit, onError)}
      requiredLabel
      beforeFooter={
        fields.length < 4 ? (
          <BsButton
            variant="primary"
            className="me-auto"
            onClick={() => append({ label: "", url: "" })}
          >
            + Ajouter un programme
          </BsButton>
        ) : undefined
      }
    >
      <Row className="gx-3">
        <Col lg={12}>
          <Form.Group className="mb-3">
            <Form.Label>
              Template d'email <span className="text-danger">*</span>
            </Form.Label>
            <Controller
              control={control}
              name="emailTemplate"
              rules={{
                required: "Veuillez sélectionner un template d'email",
              }}
              render={({ field }) => (
                <SelectPaginateAsync
                  loadOptions={loadEmailTemplatesOptions}
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.id}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Sélectionnez un template d'email..."
                  noOptionsMessage={() => "Aucun template d'email disponible"}
                />
              )}
            />
            {errors.emailTemplate && (
              <Form.Control.Feedback type="invalid" className="d-block">
                {errors.emailTemplate.message}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Col>

        <Col lg={12}>
          <Form.Group className="mb-3">
            <Form.Label>Plaquettes (facultatif)</Form.Label>
            <Controller
              control={control}
              name="brochures"
              render={({ field }) => (
                <SelectPaginateAsync
                  loadOptions={loadTrainingBrochureOptions}
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.id}
                  value={field.value}
                  onChange={field.onChange}
                  isMulti
                  placeholder="Sélectionnez une ou plusieurs plaquettes..."
                  noOptionsMessage={() => "Aucune plaquette disponible"}
                />
              )}
            />
            {errors.brochures && (
              <Form.Control.Feedback type="invalid" className="d-block">
                {errors.brochures.message}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Col>
        {fields.length > 0 && (
          <Col lg={12}>
            <Form.Label>Programmes</Form.Label>
            {fields.map((field, index) => (
              <Row key={field.id} className="gx-2 mb-2 align-items-start">
                <Col>
                  <Form.Control
                    type="text"
                    placeholder="Intitulé"
                    isInvalid={!!errors.programmes?.[index]?.label}
                    {...register(`programmes.${index}.label`, {
                      required: "L'intitulé est requis",
                    })}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.programmes?.[index]?.label?.message}
                  </Form.Control.Feedback>
                </Col>
                <Col>
                  <Form.Control
                    type="url"
                    placeholder="https://..."
                    isInvalid={!!errors.programmes?.[index]?.url}
                    {...register(`programmes.${index}.url`, {
                      required: "Le lien est requis",
                      pattern: {
                        value: /^https?:\/\/.+/,
                        message: "L'URL doit commencer par http:// ou https://",
                      },
                    })}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.programmes?.[index]?.url?.message}
                  </Form.Control.Feedback>
                </Col>
                <Col xs="auto">
                  <BsButton
                    variant="link"
                    className="text-danger p-0 mt-1"
                    onClick={() => remove(index)}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </BsButton>
                </Col>
              </Row>
            ))}
          </Col>
        )}
      </Row>

      <div className="alert alert-info mb-0 mt-3">
        <small>
          <strong>Note :</strong> L'email sera envoyé à l'adresse email configurée
          pour cette entreprise. Sans plaquette sélectionnée, il sera envoyé sans
          plaquette jointe.
        </small>
      </div>
    </Modal>
  );
};

export default forwardRef(SendCompanyBrochuresModal);
